"use client";

import { useMemo } from "react";
import { FeaturesInstanceProvider } from "@repo/next-utils/chameleon-features";
import { RouterProvider } from "@repo/next-utils/router";
import type { EnrichedCountry } from "@repo/store-presentation";
import * as Auth from "@repo/ui/auth";
import * as Bag from "@repo/ui/bag";
import * as SavedItems from "@repo/ui/saved-items";
import * as Store from "@repo/ui/store";
import { AnalyticsProvider } from "@/components/analytics/initialise-analytics";
import { ClientContextManager } from "@/components/client-context-manager";
import { FloorProvider } from "@/components/floor-context/floor-context";
import { GeoRedirectPopup } from "@/components/geo-redirect-popup";
import { APP_ENVIRONMENT, OPTIMIZELY_PROJECT_KEY } from "@/constants";
import { HeartIcon } from "@/theme/icons/heart";
import { clientRequest } from "../utils/request-adapters/client-request";

type ClientProps = {
  children: React.ReactNode;
  countryData: EnrichedCountry;
  keyStoreDataversion: string;
  shop: string;
  versionManifest: VersionManifest;
  lang: string;
  platform: string;
};

const authScopes = ["openid", "profile", "sensitive"];
const clientId = APP_ENVIRONMENT === "prod" ? "topshop" : "chameleon-www";

const appRouterPathPrefixes = [
  "/topshop/category",
  "/topman/category",
  "/product",
  "/bag",
  "/search",
  "/delivery-and-returns",
  "/wishlist",
];

export function Client({
  children,
  lang,
  platform,
  countryData,
  keyStoreDataversion,
  shop,
  versionManifest,
}: ClientProps) {
  const featureAttributes = useMemo(
    () => ({
      browseCountry: countryData.countryCode,
      language: lang,
      browseStore: countryData.storeCode,
      platform: platform,
      shop: shop,
    }),
    [countryData.countryCode, lang, countryData.storeCode, platform, shop],
  );

  return (
    <RouterProvider
      appRouterPathPrefixes={appRouterPathPrefixes}
      siteBasePath={countryData.siteBasePath}
    >
      <Store.Provider
        countryData={countryData}
        keyStoreDataversion={keyStoreDataversion}
        shop={shop}
        versionManifest={versionManifest}
      >
        <FloorProvider>
          <Auth.Provider
            clientId={clientId}
            scopes={authScopes}
            clientType="WWW"
          >
            <Bag.Provider chameleonRequestInstance={clientRequest}>
              <SavedItems.Provider
                chameleonRequestInstance={clientRequest}
                SavedIcon={<HeartIcon filled label={"Remove from wishlist"} />}
                UnsavedIcon={<HeartIcon label={"Add to wishlist"} />}
              >
                <FeaturesInstanceProvider
                  sdkKey={OPTIMIZELY_PROJECT_KEY}
                  coreAttributes={featureAttributes}
                  verbose={APP_ENVIRONMENT === "local"}
                >
                  <AnalyticsProvider>{children}</AnalyticsProvider>
                </FeaturesInstanceProvider>
              </SavedItems.Provider>
            </Bag.Provider>
          </Auth.Provider>
          <ClientContextManager />
          <GeoRedirectPopup />
        </FloorProvider>
      </Store.Provider>
    </RouterProvider>
  );
}

Client.displayName = "Client";
